import {
  CreateWorkspaceRequestSchema,
  CreateWorkspaceResponseSchema,
  type CreateWorkspaceResponse,
} from '@fundline/shared/schemas/members';
import { slugify, US_TIMEZONES } from '@fundline/shared/schemas/workspace';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { Alert } from '../../components/ui/Alert.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { SelectField, TextField } from '../../components/ui/Field.tsx';
import { Card, EmptyState, PageHeader, Spinner } from '../../components/ui/Layout.tsx';
import { queryKeys } from '../../lib/queryKeys.ts';
import { supabase } from '../../lib/supabase.ts';
import { workerApi } from '../../lib/worker-api.ts';
import { InviteLinkBox } from '../members/InviteLinkBox.tsx';

interface WorkspaceSummary {
  id: string;
  name: string;
  slug: string;
  plan: string;
  active: boolean;
  activeMembers: number;
  pendingInvites: number;
}

async function fetchWorkspaces(): Promise<WorkspaceSummary[]> {
  const client = supabase();
  const [workspaces, memberships] = await Promise.all([
    client.from('workspaces').select('id, name, slug, plan, active').order('created_at'),
    client.from('memberships').select('workspace_id, status'),
  ]);
  if (workspaces.error) throw new Error(`Couldn't load workspaces: ${workspaces.error.message}`);
  if (memberships.error) throw new Error(`Couldn't load members: ${memberships.error.message}`);
  return workspaces.data.map((w) => ({
    ...w,
    activeMembers: memberships.data.filter((m) => m.workspace_id === w.id && m.status === 'active')
      .length,
    pendingInvites: memberships.data.filter(
      (m) => m.workspace_id === w.id && m.status === 'invited',
    ).length,
  }));
}

/** Platform owner home: every workspace, read-only, plus creating a new one through the worker. */
export function PlatformPage() {
  const workspaces = useQuery({
    queryKey: queryKeys.platformWorkspaces(),
    queryFn: fetchWorkspaces,
  });
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Platform"
        description="Every Fundline workspace. You can look, not change: support access is read-only."
      />
      <section className="flex flex-col gap-3" aria-label="Workspaces">
        {workspaces.isPending ? <Spinner label="Loading workspaces" /> : null}
        {workspaces.isError ? <Alert tone="error">{workspaces.error.message}</Alert> : null}
        {workspaces.data?.length === 0 ? <EmptyState title="No workspaces yet" /> : null}
        {workspaces.data && workspaces.data.length > 0 ? (
          <ul className="flex flex-col divide-y divide-hairline rounded-lg border border-hairline bg-surface-raised">
            {workspaces.data.map((w) => (
              <li key={w.id}>
                <Link
                  to="/platform/workspaces/$workspaceId"
                  params={{ workspaceId: w.id }}
                  className="flex flex-col gap-1 p-4 hover:bg-surface focus-visible:outline-2 focus-visible:outline-focus-ring sm:flex-row sm:items-center sm:justify-between"
                >
                  <span>
                    <span className="font-medium text-ink">{w.name}</span>
                    <span className="ml-2 font-mono footnote text-ink-muted">{w.slug}</span>
                  </span>
                  <span className="callout text-ink-muted">
                    {w.activeMembers} active · {w.pendingInvites} pending · {w.plan}
                    {w.active ? '' : ' · inactive'}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : null}
      </section>
      <CreateWorkspaceCard />
    </div>
  );
}

function CreateWorkspaceCard() {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState<string | null>(null);
  const [timezone, setTimezone] = useState<string>('America/New_York');
  const [adminFullName, setAdminFullName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [created, setCreated] = useState<{
    name: string;
    admin: string;
    res: CreateWorkspaceResponse;
  } | null>(null);
  const slugValue = slug ?? slugify(name);

  const create = useMutation({
    mutationFn: (body: unknown) =>
      workerApi('/platform/workspaces', {
        method: 'POST',
        body,
        schema: CreateWorkspaceResponseSchema,
      }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.platformWorkspaces() }),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const parsed = CreateWorkspaceRequestSchema.safeParse({
      name,
      slug: slugValue,
      timezone,
      adminEmail,
      adminFullName,
    });
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])));
      return;
    }
    setErrors({});
    setCreated(null);
    create.mutate(parsed.data, {
      onSuccess: (res) => {
        setCreated({ name: parsed.data.name, admin: parsed.data.adminFullName, res });
        setName('');
        setSlug(null);
        setAdminFullName('');
        setAdminEmail('');
      },
    });
  }

  return (
    <Card title="New workspace">
      <form className="grid gap-4 sm:grid-cols-2" onSubmit={onSubmit} noValidate>
        <TextField
          label="Workspace name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={errors.name}
        />
        <TextField
          label="Address"
          hint="Lowercase letters, digits and dashes."
          value={slugValue}
          onChange={(e) => setSlug(e.target.value)}
          error={errors.slug}
        />
        <SelectField
          label="Time zone"
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          options={US_TIMEZONES}
          error={errors.timezone}
        />
        <div className="hidden sm:block" />
        <TextField
          label="First admin's name"
          value={adminFullName}
          onChange={(e) => setAdminFullName(e.target.value)}
          error={errors.adminFullName}
        />
        <TextField
          label="First admin's email"
          type="email"
          inputMode="email"
          value={adminEmail}
          onChange={(e) => setAdminEmail(e.target.value)}
          error={errors.adminEmail}
        />
        <div className="sm:col-span-2">
          <Button type="submit" busy={create.isPending}>
            Create workspace and invite admin
          </Button>
        </div>
      </form>
      <div className="mt-4 flex flex-col gap-3">
        {create.isError ? <Alert tone="error">{create.error.message}</Alert> : null}
        {created?.res.invite.inviteLink ? (
          <InviteLinkBox name={created.admin} link={created.res.invite.inviteLink} />
        ) : null}
        {created && !created.res.invite.inviteLink ? (
          <Alert tone="success">
            {created.name} was created and its admin was emailed an invite.
          </Alert>
        ) : null}
      </div>
    </Card>
  );
}
